import { Tuple } from "../types/util"

export type Note = 'C'
    | 'C#'
    | 'D'
    | 'D#'
    | 'E'
    | 'F'
    | 'F#'
    | 'G'
    | 'G#'
    | 'A'
    | 'A#'
    | 'B'

export const notes: Tuple<Note, 12> = [
    'C', 'C#', 'D', 'D#',
    'E', 'F', 'F#', 'G',
    'G#', 'A', 'A#', 'B'
]

export type Triad = 'maj' | 'min' | 'dim' | 'aug'
export const triads = ['maj', 'min', 'dim', 'aug'] as const;

export const triadSemitones: Record<Triad, Tuple<number, 3>> = {
    maj: [0, 4, 7],
    min: [0, 3, 7],
    dim: [0, 3, 6],
    aug: [0, 4, 8]
}

// const flatToSharp: Record<string, Note> = {
//     Cb: 'B',
//     Db: 'C#',
//     Eb: 'D#',
//     Fb: 'E',
//     Gb: 'F#',
//     Ab: 'G#',
//     Bb: 'A#'
// }

export type Mode = 'ionian'
    | 'dorian'
    | 'phrygian'
    | 'lydian'
    | 'mixolydian'
    | 'aeolian'
    | 'locrian'
// | 'major'
// | 'minor'
// | 'melodic'
// | 'harmonic'

export const modes: Tuple<Mode, 7> = [
    'ionian',
    'dorian',
    'phrygian',
    'lydian',
    'mixolydian',
    'aeolian',
    'locrian',
    // 'major',
    // 'minor',
    // 'melodic',
    // 'harmonic'
]

export const modePrettyNames: Record<Mode, string> = {
    'ionian': 'ionian (major)',
    'dorian': 'dorian',
    'phrygian': 'phrygian',
    'lydian': 'lydian',
    'mixolydian': 'mixolydian',
    'aeolian': 'aeolian (minor)',
    'locrian': 'locrian'
}

const prettySteps: Record<Mode, Tuple<'W' | 'H', 7>> = {
    'ionian': ['W', 'W', 'H', 'W', 'W', 'W', 'H'],
    'dorian': ['W', 'H', 'W', 'W', 'W', 'H', 'W'],
    'phrygian': ['H', 'W', 'W', 'W', 'H', 'W', 'W'],
    'lydian': ['W', 'W', 'W', 'H', 'W', 'W', 'H'],
    'mixolydian': ['W', 'W', 'H', 'W', 'W', 'H', 'W'],
    'aeolian': ['W', 'H', 'W', 'W', 'H', 'W', 'W'],
    'locrian': ['H', 'W', 'W', 'H', 'W', 'W', 'W']
}

export const modeSemitones: Record<Mode, number[]> = Object.keys(prettySteps).map((mode) => ({
    [mode]: [...prettySteps[mode as Mode].map((n: string) => n === 'W' ? 2 : 1)]
})).reduce((a, c) => ({ ...a, ...c })) as any;

export const modeTriads: Record<Mode, Tuple<Triad, 7>> = modes.map((value, offset) => {
    const base: Triad[] = 'maj min min maj maj min dim'.split(' ') as any;
    return { [value]: [...base.slice(offset, base.length), ...base.slice(0, offset)] };
}).reduce((a, c) => ({ ...a, ...c })) as any;

export const modeIntervals: Record<Mode, Tuple<string, 7>> = modes.map(mode => ({
    [mode]: 'i ii iii iv v vi vii'.split(' ').map((numeral, idx) => modeTriads[mode][idx] === 'maj' ? numeral.toUpperCase()
        : modeTriads[mode][idx] === 'dim' ? `${numeral}°`
            : modeTriads[mode][idx] === 'aug' ? `${numeral.toUpperCase()}+`
                : numeral)
})).reduce((a, c) => ({ ...a, ...c })) as any

export const midiToNote = (midi: number) => midi - 60 >= 0 ? notes[(midi-60) % notes.length] : notes.slice().reverse()[Math.abs(midi-59) % notes.length]

export function getScale(tonic: Note, mode: Mode) {
    const steps = modeSemitones[mode];
    const stepAccumulation = steps.reduce((prev, cur, idx) => [...prev, idx === 0 ? cur : cur + prev[idx-1]], [] as number[])
    const position = notes.indexOf(tonic)
    const scale = [tonic, ...[...notes.slice(position, notes.length), ...notes.slice(0, position)].filter((note, i) => stepAccumulation.indexOf(i) !== -1)]
    return scale;
}

export function noteToMidi(note: Note) {
    const positionInTonic = notes.indexOf(note)
    return positionInTonic + 60;
}

export function getMidiInversion(midiChord: number[], inversion: number) {
    let midiNotes = [...midiChord]
    for (var i = 0; i < Math.abs(inversion); ++i) {
        if (inversion >= 0) {
            midiNotes = [
                ...midiNotes.slice(1, midiNotes.length),
                midiNotes[0] + 12
            ]
        } else {
            midiNotes = [
                midiNotes[midiNotes.length - 1] - 12,
                ...midiNotes.slice(0, midiNotes.length - 1)
            ]
        }
    }
    return midiNotes;
}

export function getMidiChord(tonic: Note, mode: Mode, chordNumber: number, octave: number = 0, inversion: number = 0) {
    const semitones = triadSemitones[modeTriads[mode][chordNumber]]
    const tonicMidi = noteToMidi(tonic);
    const rootMidi = tonicMidi + modeSemitones[mode].slice(0, chordNumber).reduce((p, c) => p + c, 0) + 12 * octave
    const secondMidi = rootMidi + semitones[1]
    const thirdMidi = rootMidi + semitones[2]
    const midiNotes = getMidiInversion([rootMidi, secondMidi, thirdMidi], inversion)
    return midiNotes
}

export function getChordName(tonic: Note, mode: Mode, position: number) {
    return `${getScale(tonic, mode)[position]}${modeTriads[mode][position]}`
}

// const scales = modes.map((mode) => ({
//     [mode]: {
//         intervals: modeToInterval[mode],
//         steps: modeToSteps[mode],
//         roots: notes.map((key, offset) => ({
//             [key]: {
//                 todo: 'todo'
//             }
//         })).reduce((a, c) => ({ ...a, ...c }))
//     }
// }))